import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import axios from 'axios';
import AuthCard from './AuthCard';
import { authService } from '../../services/api';
import { Link } from 'react-router-dom';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

const ResetPasswordAuthCard = () => {
    const navigate = useNavigate();
    const { token } = useParams();
    const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
    const [status, setStatus] = useState('form'); // form | success | expired
    const [message, setMessage] = useState({ type: '', text: '' });
    const [logoUrl, setLogoUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [clientSlug, setClientSlug] = useState(null);

    useEffect(() => {
        const fetchLogo = async () => {
        try {
            const res = await axios.get('/api/settings/logo');
            if (res.data.success && res.data.logoUrl) {
                setLogoUrl(res.data.logoUrl);
            }
            else 
            {                
                setLogoUrl('/images/beetsol-logo.png');
            }
            setClientSlug(res.data.slug || null);
        } catch (err) {
            console.error('Failed to fetch logo', err);
        }
        };

        fetchLogo();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (formData.password !== formData.confirmPassword) {
        setMessage({ type: 'error', text: 'Passwords do not match' });
        return;
        }

        setIsLoading(true);

        try {
        await authService.resetPassword(token, formData.password, formData.confirmPassword);
        setStatus('success');
        //setTimeout(() => navigate('/login'), 3000);
        } catch (error) {
        const text = error.response?.data?.message || 'Password reset failed';
        if (/invalid|expired/i.test(text)) {
            setStatus('expired');
        } else {
            setMessage({ type: 'error', text });
        }
        } finally {
        setIsLoading(false);
        }
    };

    if (status === 'success') {
        return (
        <AuthCard logoUrl={logoUrl}>
            <div className="ac-top">
                <div className="ac-icon ac-icon-success">
                    <span style={{ fontSize: '24px' }}>
                        <CheckOutlinedIcon />
                    </span>
                </div>
                 <div className="ac-title">Password updated</div>
                <div className="ac-sub">Your password has been reset. You can now sign in with your new password.</div>
            </div>
            <div className="ac-body">               
                <button className="ac-btn-primary" onClick={() => navigate(`/learn/${clientSlug}`)}>
                    Sign in
                </button>
            </div>
            <div className="ac-footer" />
        </AuthCard>
        );
    }

    if (status === 'expired') {
        return (
        <AuthCard logoUrl={logoUrl} badge="Reset password">
            <div className="ac-top">
                <div className="ac-icon ac-icon-warn">
                    <span style={{ fontSize: '24px' }}>
                        <WarningAmberOutlinedIcon />
                    </span>
                </div>
                <div className="ac-title">Reset link expired</div>
                <div className="ac-sub">
                    This password reset link has expired. Reset links are valid for 30 minutes.
                </div>
            </div>
            <div className="ac-body">          
                <button className="ac-btn-primary" onClick={() => navigate('/forgot')}>
                    Request new reset link
                </button>
                <div className="ac-field-link">
                    <Link to={`/learn/${clientSlug}`} onClick={() => setStatus('email-input')} className="ac-link">
                        ← Back to sign in
                    </Link>
                </div>
            </div>
            <div className="ac-footer" />
        </AuthCard>
        );
    }

    return (
        <AuthCard logoUrl={logoUrl} badge="Internal account">
            <div className="ac-top">
                <div className="ac-title">Set new password</div>
                <div className="ac-sub">Choose a new password for your account.</div>
            </div>

            <div className="ac-body">
                {message.text && (
                <div className={`ac-box ${message.type === 'success' ? 'ac-box-success' : 'ac-box-error'}`}>
                    <div className="ac-box-sub">{message.text}</div>
                </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="ac-field">
                        <div className="ac-field-label">New password</div>
                        <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="ac-field-input"
                        placeholder="New password"
                        required
                        />
                    </div>

                    <div className="ac-field">
                        <div className="ac-field-label">Confirm password</div>
                        <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        className="ac-field-input"
                        placeholder="Confirm password"
                        required
                        />
                    </div>

                    <button type="submit" className="ac-btn-primary" disabled={isLoading}>
                        {isLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Reset password'}
                    </button>
                </form>
            </div>

            <div className="ac-footer" />
        </AuthCard>
    );
};

export default ResetPasswordAuthCard;